import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { RecruitingService } from '../../../../../../api/recruiting.service';
import { CustomerLogForStaff } from '../../../../../../api/types/partner';
import { DateTime } from 'luxon';
import { DataPadService } from 'src/app/ui/data-pad/data-pad.service';
import { IconService } from 'src/app/ui/icon.service';
import { UiModule } from 'src/app/ui/ui.module';
import { EditScheduledActivityComponentInputs } from 'src/app/component-modules/notes/edit-scheduled-activity/edit-scheduled-activity.component';
import { of, Subscription } from 'rxjs';
import { Contact } from 'src/app/api/types/contacts';
import { TripDetail } from 'src/app/api/commonInterfaces';
import { LocalAuthService } from 'src/app/auth/local-auth.service';
import { Timeframe } from 'src/app/api/types/recruiting';
import { NotesModule } from 'src/app/component-modules/notes/notes.module';
import { NotesService } from 'src/app/api/notes.service';
import { Note } from 'src/app/api/commonInterfaces';
import { ErrorService } from 'src/app/services/error.service';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-recruiting-scheduled-activities',
  templateUrl: './recruiting-scheduled-activities.component.html',
  styleUrls: ['./recruiting-scheduled-activities.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, UiModule, NotesModule]
})
export class RecruitingScheduledActivitiesComponent implements OnInit, OnDestroy {
  activities = signal<CustomerLogForStaff[]>([]);
  loading = true;
  error: string | null = null;
  showAddActivityButton = false;

  @Input() staffId: number = 0;
  @Input() timeframe: Timeframe = 'week';
  @Input() maxHeight: number = 0;

  constructor(
    private recruitingService: RecruitingService,
    private dataPadService: DataPadService,
    public icons: IconService,
    private auth: LocalAuthService,
    private notesService: NotesService,
    private errorService: ErrorService,
    private messageService: MessageService
  ) { }

  private subscriptions: Subscription[] = [];

  ngOnInit() {
    if (!this.staffId) return;
    this.showAddActivityButton = this.auth.currentUser?.newstaffId === this.staffId;

    // Keep the list in sync when activities are completed/edited/deleted
    this.subscriptions.push(
      this.notesService.scheduledActivitiesUpdated$.subscribe(args => {
        if (!args) return;

        if (args.operation === 'completed') {
          this.activities.set(this.activities().filter(a => a.id !== args.noteId));
          this.messageService.add({
            severity: 'success',
            summary: 'Task completed',
            detail: 'The activity was marked as completed.'
          });
          return;
        }

        if (args.operation === 'removed') {
          this.activities.set(this.activities().filter(a => a.id !== args.noteId));
          this.messageService.add({
            severity: 'warn',
            summary: 'Task removed',
            detail: 'The activity was deleted.'
          });
          return;
        }

        if (args.operation === 'updated') {
          this.messageService.add({
            severity: 'info',
            summary: 'Task updated',
            detail: 'The activity was updated.'
          });
          // Keep it in the scheduled list; just refresh ordering/content.
          this.loadActivities(this.staffId);
          return;
        }

        // For add/edit we may need to re-sort / re-render based on new dateScheduled.
        this.loadActivities(this.staffId);
      })
    );

    this.loadActivities(this.staffId);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private loadActivities(staffId: number) {
    this.loading = true;
    this.recruitingService.getScheduledActivitiesForStaff(staffId, this.timeframe).subscribe({
      next: (data) => {
        this.activities.set(data);
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Failed to load activities';
        this.loading = false;
        console.error('Error loading activities:', err);
      }
    });
  }

  setTitle() {
    switch (this.timeframe) {
      case "day": return "Today's Recruiting Activities";
      case "week": return "This Week's Recruiting Activities";
      case "month": return "This Month's Recruiting Activities";
      case "year": return "This Year's Recruiting Activities";
      case "all": return "All Recruiting Activities";
    }
  }

  setNoActivitiesMessage() {
    switch (this.timeframe) {
      case "day": return "No activities scheduled for today";
      case "week": return "No activities scheduled for this week";
      case "month": return "No activities scheduled for this month";
      case "year": return "No activities scheduled for this year";
      case "all": return "No activities scheduled";
    }
  }

  formatDatetime(date: Date): string {
    // Ensure the date is properly handled as a Date object
    const dateObj = date instanceof Date ? date : new Date(date);
    return DateTime.fromJSDate(dateObj).toFormat('MM/dd/yyyy hh:mm a');
  }

  addActivity() {
    this.dataPadService.showPanel<EditScheduledActivityComponentInputs>(
      { 
        panelName: "editScheduledActivity", 
        inputData: { 
          tripDetails: [], 
          custId: 0, 
          contacts: [], 
          inputObservables: { 
            contacts: of<Contact[]>([]), 
            tripDetails: of<TripDetail[]>([]) 
          } 
        } 
      }
    )
  }

  editActivity(activity: Note) {
    this.notesService.getCustomerDetailsForNoteEdit(activity.custId).subscribe({
      next: (results) => {
        this.dataPadService.showPanel<EditScheduledActivityComponentInputs>({
          panelName: "editScheduledActivity",
          inputData: {
            tripDetails: results.tripDetails,
            custId: activity.custId,
            contacts: results.contacts,
            editActivity: activity
          }
        })
      },
      error: (err) => {
        this.errorService.error('Failed to load customer details', err, this.messageService)
      }
    })
  }

  completeActivity(activity: Note) {
    this.notesService.getCustomerDetailsForNoteEdit(activity.custId).subscribe({
      next: (results) => {
        this.dataPadService.showPanel({
          panelName: "completeScheduledActivity",
          inputData: {
            tripDetails: results.tripDetails,
            contacts: results.contacts,
            editActivity: activity,
            custId: activity.custId
          }
        })
      },
      error: (err) => {
        this.errorService.error('Failed to load customer details', err, this.messageService)
      }
    })
  }

  deleteActivityClicked(note: Note) {
    this.dataPadService.showPanel({
      panelName: "deleteNote",
      inputData: { note },
      outputs: { yesClick: (noteToDelete: Note) => this.deleteActivity(noteToDelete) }
    })
  }

  private deleteActivity(note: Note) {
    this.notesService.deleteNote(note.id).subscribe({
      next: () => {
        this.activities.set(this.activities().filter(a => a.id !== note.id))
      },
      error: (err) => {
        this.errorService.error('There was a problem deleting the scheduled activity', err, this.messageService)
      }
    })
  }
} 